import React from 'react';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { PlayArrow, CalendarMonth } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const colors = {
  cobaltDeep: '#1a2f6c',
  cobaltMain: '#2b4cc0',
  cobaltBright: '#5a7dff',
  cobaltSky: '#7d9fff',
  cobaltIce: '#b3c9ff',
  midnight: '#0f1a34',
  frost: '#f5f7ff',
};

const HeroSection: React.FC = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(165deg, ${colors.midnight} 0%, ${colors.cobaltDeep} 100%)`,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/landing/hero-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.3,
          zIndex: 0,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '50%',
          height: '50%',
          background: `radial-gradient(circle, ${colors.cobaltBright}40 0%, transparent 70%)`,
          filter: 'blur(80px)',
          zIndex: 1,
        },
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          py: { xs: 8, md: 12 },
        }}
      >
        <Stack spacing={4} alignItems="center" textAlign="center">
          {/* 메인 헤드라인 */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem', lg: '5rem' },
              fontWeight: 700,
              background: `linear-gradient(135deg, ${colors.frost} 0%, ${colors.cobaltIce} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            중소제조업의 미래를 여는
            <br />
            스마트 금형 관리
          </Typography>

          {/* 서브 헤드라인 */}
          <Typography
            variant="h5"
            sx={{
              color: colors.cobaltIce,
              fontWeight: 400,
              maxWidth: '800px',
              fontSize: { xs: '1.1rem', md: '1.4rem' },
              lineHeight: 1.6,
            }}
          >
            QR코드, 실시간 수명 예측, AI 리포트로
            <br />
            낭비되던 시간과 비용을 <strong>90% 절감</strong>하고
            <br />
            데이터 기반의 스마트 팩토리를 시작하세요.
          </Typography>

          {/* CTA 버튼 */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mt: 4 }}
          >
            <Button
              component={Link}
              to="/login"
              variant="contained"
              size="large"
              startIcon={<PlayArrow />}
              sx={{
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '50px',
                background: `linear-gradient(120deg, ${colors.cobaltBright}, ${colors.cobaltSky})`,
                color: colors.frost,
                boxShadow: `0 0 40px ${colors.cobaltBright}80, 0 10px 30px ${colors.cobaltMain}50`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 0 50px ${colors.cobaltBright}90, 0 15px 40px ${colors.cobaltMain}60`,
                },
              }}
            >
              무료 체험 시작
            </Button>

            <Button
              component="a"
              href="#contact"
              variant="outlined"
              size="large"
              startIcon={<CalendarMonth />}
              sx={{
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '50px',
                borderWidth: 2,
                borderColor: colors.cobaltSky,
                color: colors.cobaltIce,
                backdropFilter: 'blur(10px)',
                background: `${colors.cobaltMain}20`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderWidth: 2,
                  borderColor: colors.cobaltBright,
                  background: `${colors.cobaltBright}30`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              맞춤 데모 신청
            </Button>
          </Stack>

          {/* 신뢰 지표 */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={4}
            sx={{ mt: 6 }}
          >
            {[
              { label: '도입 기업', value: '50+' },
              { label: '시간 절감', value: '90%' },
              { label: 'ROI', value: '2,196%' },
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  px: 3,
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    color: colors.cobaltBright,
                    fontWeight: 700,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                  }}
                >
                  {item.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.cobaltIce,
                    fontSize: '0.9rem',
                    mt: 0.5,
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default HeroSection;
